import type { DownloadIdentity } from '@mimir/client-common/fileSync/downloadIdentity';
import { getOngoingDownloadId } from '@mimir/client-common/model/ongoingDownload';
import type { DownloadReporter } from '@mimir/client-common/model/ongoingDownload';

/**
 * Contains all the download progress reporters installed by the sync page table
 * cells, keyed by ongoing download IDs.
 */
export const downloadProgressReporterMap: Record<string, DownloadReporter[]> = {};

/**
 * The current download progresses, if any, keyed by ongoing download IDs.
 */
export const downloadProgressMap: Record<string, number> = {};


function reportProgress(downloadId: string, percentage: number) {
  downloadProgressMap[downloadId] = percentage;
  (downloadProgressReporterMap[downloadId] ?? []).forEach(r => r.onProgress(percentage));
}


function reportSuccess(downloadId: string) {
  delete downloadProgressMap[downloadId];
  (downloadProgressReporterMap[downloadId] ?? []).forEach(r => r.onSuccess());
}


function reportReject(downloadId: string, cause: unknown) {
  delete downloadProgressMap[downloadId];
  (downloadProgressReporterMap[downloadId] ?? []).forEach(r => r.onReject(cause));
}


function reportActivity(downloadId: string, activity: string) {
  (downloadProgressReporterMap[downloadId] ?? []).forEach(r => r.onActivity(activity));
}


export function addDownloadProgressReporter(downloadId: string, r: DownloadReporter) {
  if (downloadProgressReporterMap[downloadId] === undefined) {
    downloadProgressReporterMap[downloadId] = [r];
  } else {
    downloadProgressReporterMap[downloadId].push(r);
  }

  if (downloadProgressMap[downloadId] !== undefined) {
    // If a user starts a download from the Premiere sync page,
    // goes away and then comes back, we want to make sure
    // the download progress reporters freshly created by the sync page cells
    // are going to be initialized with the current progress
    r.onProgress(downloadProgressMap[downloadId]);
  }
}


export function removeDownloadProgressReporter(downloadId: string, r: DownloadReporter) {
  if (downloadProgressReporterMap[downloadId] === undefined) {
    return;
  }
  const pos = downloadProgressReporterMap[downloadId].indexOf(r);
  if (pos !== -1) {
    downloadProgressReporterMap[downloadId].splice(pos, 1);
    if (!downloadProgressReporterMap[downloadId].length) {
      delete downloadProgressReporterMap[downloadId];
    }
  }
}


export function makeDownloadProgressReporter(downloadIdentity: DownloadIdentity): DownloadReporter {
  const downloadId = getOngoingDownloadId(downloadIdentity, null);
  // Let's report 0% progress immediately in order to notify the UI components
  // that the preparation for the download operation has begun
  reportProgress(downloadId, 0);
  return {
    onSuccess() {
      reportSuccess(downloadId);
    },
    onReject(cause: unknown) {
      reportReject(downloadId, cause);
    },
    onProgress(percentage: number) {
      reportProgress(downloadId, percentage);
    },
    onActivity(activity: string) {
      reportActivity(downloadId, activity);
    },
  };
}
